import { PermissionCategory, PermissionLevel } from '@/client/custom-roles';
import { PermissionRow } from '@/components/custom-roles/PermissionRow';
import { RoleSummaryCard } from '@/components/custom-roles/RoleSummaryCard';
import { ScopeModePanel } from '@/components/custom-roles/ScopeModePanel';
import {
  CustomRoleFormValues,
  customRoleSchema,
  requiresScopeMode,
} from '@/components/custom-roles/forms/validations/custom-roles';
import { FormikInput } from '@/components/form/FormikInput';
import { PERMISSION_CATEGORY_ORDER } from '@/utils/custom-roles';
import { Field, Form, Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const Layout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--semi-big-padding); /* Figma 35px */
  width: 100%;

  @media screen and (max-width: 960px) {
    flex-direction: column;
  }
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--default-padding); /* Figma 23px -> --default-padding (24px) */
  flex: 1;
  min-width: 0;
  padding: var(--medium-big-padding); /* Figma 28px -> 30px */
  background-color: var(--white-main);
  border-radius: var(--semi-medium-border-radius);
`;

const BlockHeader = styled.div`
  display: flex;
  flex-direction: column;
`;

const BlockTitle = styled.h2`
  margin: 0;
  font-size: var(--medium-font-size); /* Figma 16px */
  font-weight: var(--semibold-font-weight);
  line-height: var(--small-line-height);
`;

const BlockSubtitle = styled.p`
  margin: 0;
  font-size: var(--small-font-size);
  font-weight: var(--regular-font-weight);
  line-height: var(--small-line-height);
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--medium-padding); /* Figma 15px -> --medium-padding (16px) */
`;

export const emptyCustomRoleFormValues = (): CustomRoleFormValues => ({
  name: '',
  scopeMode: null,
  permissions: PERMISSION_CATEGORY_ORDER.reduce(
    (levels, category) => ({ ...levels, [category]: PermissionLevel.NONE }),
    {} as Record<PermissionCategory, PermissionLevel>,
  ),
});

type CustomRoleFormProps = {
  initialValues: CustomRoleFormValues;
  assignedUserCount: number;
  isSaving: boolean;
  onSubmit: (values: CustomRoleFormValues) => void;
  onCancel: () => void;
};

export const CustomRoleForm: React.FC<CustomRoleFormProps> = ({
  initialValues,
  assignedUserCount,
  isSaving,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={customRoleSchema(t)}
      onSubmit={(values) =>
        onSubmit({
          ...values,
          name: values.name.trim(),
          scopeMode: requiresScopeMode(values.permissions)
            ? values.scopeMode
            : null,
        })
      }
    >
      {({ values, errors, submitCount, setFieldValue, submitForm }) => (
        <Form autoComplete="off">
          <Layout>
            <Main>
              <Field
                id="name"
                name="name"
                label={t('account.rolesAccess.builder.name.label')}
                placeholder={t('account.rolesAccess.builder.name.placeholder')}
                className="w-full"
                component={FormikInput}
                required
              />

              {requiresScopeMode(values.permissions) && (
                <ScopeModePanel
                  value={values.scopeMode}
                  error={submitCount > 0 ? errors.scopeMode : undefined}
                  onChange={(mode) => setFieldValue('scopeMode', mode)}
                />
              )}

              <BlockHeader>
                <BlockTitle>
                  {t('account.rolesAccess.builder.permissions.title')}
                </BlockTitle>
                <BlockSubtitle>
                  {t('account.rolesAccess.builder.permissions.subtitle')}
                </BlockSubtitle>
              </BlockHeader>

              <Rows>
                {PERMISSION_CATEGORY_ORDER.map((category) => (
                  <PermissionRow key={category} category={category} />
                ))}
              </Rows>
            </Main>

            <RoleSummaryCard
              values={values}
              assignedUserCount={assignedUserCount}
              isSaving={isSaving}
              onSave={submitForm}
              onCancel={onCancel}
            />
          </Layout>
        </Form>
      )}
    </Formik>
  );
};
